import { Filtering } from '@ekkogmbh/apisdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContextHardened } from '../Helper/ContextHelper';

export type DataTableRecord<T = object> = T;
export type DataTableData<T = object> = DataTableRecord<T>[];

interface Context {
  data: DataTableData;
  totalItemCount: number;
  selectedData: DataTableData;
  isFilterSelect: boolean;
  setData: (data: DataTableData[]) => void;
  setTotalItemCount: (count: number) => void;
  setRowSelected: (index: number, selected?: boolean) => void;
  isRowSelected: (index: number) => boolean;
  toggleRowSelected: (index: number) => void;
  /** Header checkbox 3-state cycle: none → all on page → all via filter → none. */
  toggleAllSelected: () => void;
  /** Clear both per-row selection and the filter-select flag. */
  clearSelection: () => void;

  // Sort state
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  setSortBy: (by?: string) => void;
  setSortDirection: (dir?: 'asc' | 'desc') => void;

  /**
   * Snapshot of the filter from the last search execution.
   * Used for highlights and for the `filtering` object exposed to API consumers.
   */
  committedFilter: Record<string, string>;
  commitFilter: (filter: Record<string, string>) => void;

  /** Computed Filtering object for external consumers (API calls). */
  filtering: Filtering;

  /**
   * Search action handlers registered by DataTable, called by NavigationSearchBar.
   * Null when no DataTable is mounted (e.g. non-table pages).
   */
  triggerSearch: (() => void) | null;
  clearSearch: (() => void) | null;
  registerSearchHandlers: (trigger: () => void, clear: () => void) => void;

  /** True when any column filter input has draft text. Set by DataTable. */
  hasColumnFilterInput: boolean;
  setHasColumnFilterInput: (has: boolean) => void;

  /** True when the global search input has draft text. Set by DataTable. */
  isSearchActive: boolean;
  setIsSearchActive: (active: boolean) => void;

  /** Per-field draft filter values. Owned by DataTable but stored here so
      DataTableHeader can read values directly from context without going
      through the column definition pipeline (which would cause full table
      re-renders on every keystroke). */
  fieldsFilter: Record<string, string>;
  setFieldsFilter: React.Dispatch<React.SetStateAction<Record<string, string>>>;

  /** Clears column filter draft; commits + refetches only if filters
      were committed. Registered by DataTable, null when unmounted. */
  clearColumnFilters: (() => void) | null;
  registerClearColumnFilters: (fn: () => void) => void;
}
const [DataTableContext, useDataTable] = createContextHardened<Context>('Data-Table');
export default useDataTable;

interface Props {
  children: React.ReactNode;
}
export const DataTableProvider = ({ children }: Props): React.JSX.Element => {
  const [data, setData] = useState<DataTableData>([]);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [selectMask, setSelectMask] = useState<boolean[]>([]);
  const [isFilterSelect, setFilterSelect] = useState<boolean>(false);

  // =========================================================
  // Sort & Filter State
  // =========================================================

  const [sortBy, setSortBy] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>();
  const [committedFilter, setCommittedFilter] = useState<Record<string, string>>({});

  const commitFilter = useCallback((filter: Record<string, string>) => {
    setCommittedFilter(filter);
  }, []);

  const filtering = useMemo(
    (): Filtering => ({
      sort: sortBy,
      direction: sortDirection,
      filter: Object.keys(committedFilter).length > 0 ? committedFilter : undefined,
    }),
    [sortBy, sortDirection, committedFilter],
  );

  // =========================================================
  // Search Handlers (registered by DataTable, called by NavigationSearchBar)
  // =========================================================

  const [triggerSearch, setTriggerSearch] = useState<(() => void) | null>(null);
  const [clearSearch, setClearSearch] = useState<(() => void) | null>(null);

  const registerSearchHandlers = useCallback((trigger: () => void, clear: () => void) => {
    setTriggerSearch(() => trigger);
    setClearSearch(() => clear);
  }, []);

  // =========================================================
  // Column Filter Brokering (mutual exclusivity with global search)
  // =========================================================

  const [hasColumnFilterInput, setHasColumnFilterInput] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [fieldsFilter, setFieldsFilter] = useState<Record<string, string>>({});
  const [clearColumnFilters, setClearColumnFilters] = useState<(() => void) | null>(null);
  const registerClearColumnFilters = useCallback((fn: () => void) => {
    setClearColumnFilters(() => fn);
  }, []);

  // =========================================================
  // Row Selection
  // =========================================================

  const setAllSelected = useCallback(
    (selected: boolean = true) => setSelectMask(Array(data.length).fill(selected)),
    [data],
  );

  /** clear select mask when data changes */
  useEffect(() => {
    setAllSelected(false);
  }, [setAllSelected]);

  const selectedData = useMemo(() => data.filter((_, idx) => selectMask[idx]), [data, selectMask]);

  const setRowSelected = useCallback(
    (index: number, selected: boolean = true) => {
      selectMask[index] = selected;
      setSelectMask([...selectMask]);
    },
    [selectMask],
  );

  const toggleAllSelected = useCallback(() => {
    const isAllSelected = selectMask.length > 0 && selectMask.every((r) => r);
    if (isAllSelected) {
      if (isFilterSelect) {
        setFilterSelect(false);
        setAllSelected(false);
      } else {
        setFilterSelect(true);
      }
    } else {
      setAllSelected(true);
    }
  }, [selectMask, isFilterSelect, setAllSelected]);

  const clearSelection = useCallback(() => {
    setFilterSelect(false);
    setAllSelected(false);
  }, [setAllSelected]);

  const isRowSelected = useCallback(
    (index: number) => {
      if (isFilterSelect) {
        return true;
      }

      return selectMask.at(index) ?? false;
    },
    [selectMask, isFilterSelect],
  );

  const toggleRowSelected = useCallback(
    (index: number) => {
      const isSelected = isRowSelected(index);
      setRowSelected(index, !isSelected);
    },
    [isRowSelected, setRowSelected],
  );

  return (
    <DataTableContext.Provider
      value={{
        data,
        totalItemCount,
        selectedData,
        isFilterSelect,
        setData,
        setTotalItemCount,
        setRowSelected,
        isRowSelected,
        toggleRowSelected,
        toggleAllSelected,
        clearSelection,
        sortBy,
        sortDirection,
        setSortBy,
        setSortDirection,
        committedFilter,
        commitFilter,
        filtering,
        triggerSearch,
        clearSearch,
        registerSearchHandlers,
        hasColumnFilterInput,
        setHasColumnFilterInput,
        isSearchActive,
        setIsSearchActive,
        fieldsFilter,
        setFieldsFilter,
        clearColumnFilters,
        registerClearColumnFilters,
      }}
    >
      {children}
    </DataTableContext.Provider>
  );
};
